import useHttpProvider from "@/common/hooks/use-http";
import { ResponseError } from "@/common/models/error";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { getMetaAttributionValues } from "@/common/services/analytics/attribution";
import { trackLead } from "@/common/services/analytics";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import { Plan } from "@/features/payments/models/plan";
import * as CheckoutServices from "@/features/payments/services/checkout";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { User } from "../models/user";
import { HomeRoute } from "@/features/home/route";
import * as AuthenticationService from "../services/authentication";
import useActivateAccount from "./use-active-account";

const passwordPattern = /^(?=.*\d).{8,}$/;

const FormSchema = z.object({
	email: z
		.string()
		.email("Invalid email address")
		.refine((value) => !value.includes("+"), {
			message: "Invalid email address",
		}),
	password: z
		.string({
			message: "Password is required",
		})
		.min(8, "Password must be at least 8 characters long")
		.regex(passwordPattern, "Password must contain at least one number"),
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	acceptedTerms: z.literal<boolean>(true, {
		errorMap: () => ({
			message: "You must accept the Terms and Privacy Policy to continue",
		}),
	}),
});

export type SignUpStep = "details" | "plans";

function useSignUp() {
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
	});

	const navigate = useNavigate();
	const location = useLocation();

	const { http } = useHttpProvider();

	const [step, setStep] = useState<SignUpStep>("details");
	const [selectedPlan, setSelectedPlan] = useState<Plan>();
	const [checkoutClientSecret, setCheckoutClientSecret] = useState<string>();
	const [appliedPromotionCode, setAppliedPromotionCode] = useState<string | null>(null);
	const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [captchaToken, setCaptchaToken] = useState<string | undefined>();

	const acceptedTerms = form.watch("acceptedTerms") === true;

	const {
		onResendVerificationCode,

		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,
	} = useActivateAccount(onAccountActivated);

	function onInvalidSubmit(
		errors: Partial<Record<keyof z.infer<typeof FormSchema>, unknown>>
	) {
		if (errors.acceptedTerms) {
			notifyError(
				"Terms required",
				"Please accept the Terms and Privacy Policy to continue."
			);
		}
	}

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		setIsSubmitting(true);

		try {
			const { email, password, firstName, lastName, acceptedTerms } = data;
			const metaLeadEventId = `lead_${Date.now()}_${Math.random()
				.toString(36)
				.slice(2, 10)}`;
			const { fbc, fbp } = getMetaAttributionValues();

			const response = await AuthenticationService.signup(
				http,
				email,
				password,
				firstName,
				lastName,
				acceptedTerms,
				captchaToken,
				metaLeadEventId,
				fbc,
				fbp
			);

			await AuthenticationProvider.setUser(response);
			trackLead({
				content_name: "signup_started",
			}, {
				eventID: metaLeadEventId,
			});

			activateAccountForm.reset();
			setShowActivateAccountDialog(true);
		} catch (error: unknown) {
			const detail = (error as ResponseError).detail;

			if (detail === "invalid_email") {
				notifyError("Invalid email", "Please enter a valid email address.");
				form.setError("email", {
					type: "manual",
					message: "Please enter a valid email address.",
				});
				return;
			}

			if (detail === "invalid_password") {
				notifyError("Invalid password", "Please enter a valid password");
				form.setError("password", {
					type: "manual",
					message: "Invalid password",
				});
				return;
			}

			if (detail === "email_is_already_taken") {
				notifyError("Email exists", "This email is already in use.");
				form.setError("email", {
					type: "manual",
					message: "Email already exists.",
				});
				return;
			}

			if (detail === "invalid_captcha_token") {
				notifyError(
					"Captcha verification failed",
					"Please complete the captcha and try again."
				);
				setCaptchaToken(undefined);
				return;
			}

			if (detail === "terms_not_accepted") {
				notifyError(
					"Terms required",
					"Please accept the Terms and Privacy Policy to continue."
				);
				form.setError("acceptedTerms", {
					type: "manual",
					message: "You must accept the Terms and Privacy Policy to continue",
				});
				return;
			}
		} finally {
			setIsSubmitting(false);
		}
	}

	async function onAccountActivated(user: User | null) {
		notifySuccess("Account creation", "Account created successfully");

		if (user) {
			await AuthenticationProvider.signin(user);
		}

		const params = new URLSearchParams(location.search);
		if (params.get("skipPlans") === "true") {
			navigateAfterSignup();
			return;
		}

		setStep("plans");
	}

	async function onGoogleCredential(accessToken: string) {
		setIsSubmitting(true);
		try {
			const metaLeadEventId = `lead_${Date.now()}_${Math.random()
				.toString(36)
				.slice(2, 10)}`;
			const { fbc, fbp } = getMetaAttributionValues();

			const user = await AuthenticationService.googleSignIn(
				http,
				accessToken,
				"signup",
				metaLeadEventId,
				fbc,
				fbp,
				form.getValues("acceptedTerms") === true
			);

			trackLead(
				{ content_name: "signup_started" },
				{ eventID: metaLeadEventId }
			);

			await onAccountActivated(user);
		} catch (error: unknown) {
			const detail =
				typeof error === "object" && error !== null && "detail" in error
					? (error as { detail?: string }).detail
					: undefined;

			if (detail === "terms_not_accepted") {
				notifyError(
					"Terms required",
					"Please accept the Terms and Privacy Policy to continue."
				);
				form.setError("acceptedTerms", {
					type: "manual",
					message: "You must accept the Terms and Privacy Policy to continue",
				});
			} else {
				notifyError("Google sign-in failed", "Please try again.");
			}
		} finally {
			setIsSubmitting(false);
		}
	}

	function navigateAfterSignup() {
		const params = new URLSearchParams(location.search);
		const from = params.get("from");

		if (from) {
			sessionStorage.setItem("returningFromSignup", "true");
			navigate(from, { replace: true });
			return;
		}

		navigate(HomeRoute, { replace: true });
	}

	function onSkipPlan() {
		navigateAfterSignup();
	}

	async function createCheckout(promotionCode?: string) {
		if (!selectedPlan) return;

		const userId = AuthenticationProvider.user?.id;
		if (!userId) return;

		setIsCreatingCheckout(true);
		try {
			const result = await CheckoutServices.createSubscriptionPaymentIntent(
				http,
				HomeRoute,
				userId,
				selectedPlan.stripePriceId,
				selectedPlan.id,
				{
					value: Number(selectedPlan.price),
					currency: "AUD",
					contentName: selectedPlan.name,
					contentIds: [`plan_${selectedPlan.id}`],
					numItems: 1,
				},
				promotionCode
			);

			setCheckoutClientSecret(result.clientSecret);
			setAppliedPromotionCode(result.promotionCodeApplied ?? null);
		} catch (error: unknown) {
			const detail = (error as { detail?: string })?.detail;
			if (detail === "invalid_promotion_code") {
				notifyError("Coupon invalid", "That code isn't valid or has expired.");
			} else {
				notifyError(
					"Checkout failed",
					"Could not start checkout. Please try again."
				);
			}
			throw error;
		} finally {
			setIsCreatingCheckout(false);
		}
	}

	async function onContinueToCheckout() {
		try {
			await createCheckout();
		} catch {
			// already toasted
		}
	}

	async function applyPromotionCode(code: string) {
		try {
			await createCheckout(code);
			notifySuccess("Coupon applied", "Discount has been added to your order.");
		} catch {
			// already toasted
		}
	}

	async function removePromotionCode() {
		try {
			await createCheckout();
		} catch {
			// already toasted
		}
	}

	function onCheckoutComplete() {
		navigateAfterSignup();
	}

	function onBackToPlans() {
		setCheckoutClientSecret(undefined);
		setAppliedPromotionCode(null);
	}

	function checkForPreSelectedPlan() {
		const preSelectedPlan = location.state?.plan;
		if (preSelectedPlan) {
			setSelectedPlan(preSelectedPlan);
		}
	}

	useEffect(() => {
		checkForPreSelectedPlan();
	}, []);

	return {
		step,

		selectedPlan,
		setSelectedPlan,

		form,
		onSubmit,
		onInvalidSubmit,

		onResendVerificationCode,
		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,

		checkoutClientSecret,
		isCreatingCheckout,
		onContinueToCheckout,
		onSkipPlan,
		onCheckoutComplete,
		onBackToPlans,
		appliedPromotionCode,
		applyPromotionCode,
		removePromotionCode,

		isSubmitting,

		captchaToken,
		setCaptchaToken,

		acceptedTerms,

		onGoogleCredential,
	};
}

export default useSignUp;
